import { createAction } from '@reduxjs/toolkit';
import type {
  FlowFestProject,
  FlowFestCard,
  FlowFestProjectId,
  FlowFestPack,
} from '@starly/starly-types';
import type { FilterOption } from '../marketplace/marketplaceTypes';

export type ExtendedFFCard = FlowFestCard & {
  price: number,
  storefrontAddress: string,
  ftType: 'FLOW' | 'FUSD',
  projectName: string,
  listingResourceID: number,
  isBuying?: boolean,
  isForSale?: boolean,
};

type FFBuyCardRequestPayload = {
  project: string,
  listingResourceID: number,
  storefrontAddress: string,
  buyPrice: number
};

type FFOpenPackResponse = {
  cards: FlowFestCard[];
  pack: FlowFestPack;
};

type FFShowPackResponse = {
  cards: FlowFestCard[];
  packs: FlowFestPack[];
};

type FFViewCardRequestPayload = {
  pack_id: string;
  card_id: string;
};

type FFViewPackRequestPayload = {
  pack_id: string;
};

type RemoveFromSaleFFCardRequest = {
  id: number,
  userWallet: string,
};

type GetFFPackStateResponse = {
  viewed: boolean,
};

type LoadMyFFCardsResponse = {
  cards: ExtendedFFCard[],
};

export const flowFestProjectRequest = createAction<{ id: string }>('flowFest/flowProjectRequest');
export const flowFestProjectResponse = createAction<{ project: FlowFestProject }>('flowFest/flowProjectResponse');

export const loadFFProjectsRequest = createAction('flowFest/loadFFProjectsRequest');
export const loadFFProjectsResponse = createAction<{ projects: FlowFestProject[] }>('flowFest/loadFFProjectsResponse');
export const loadFFProjectsFailure = createAction<{ error: string }>('flowFest/loadFFProjectsFailure');

export const loadFFCardsRequest = createAction<{
  filter?: string,
  sort?: string,
  rarity?: FilterOption[]
  isResetCursor: boolean,
}>('flowFest/loadFFCardsRequest');
export const loadFFCardsResponse = createAction<{ cards: ExtendedFFCard[], isLastCards: boolean }>('flowFest/loadFFCardsResponse');
export const loadFFCardsFailure = createAction<{ error: string }>('flowFest/loadFFCardsFailure');

export const buyFFCardRequest = createAction<FFBuyCardRequestPayload>('flowFest/buyFFCardRequest');
export const buyFFCardResponse = createAction<{ listingResourceID: number }>('flowFest/buyFFCardResponse');
export const buyFFCardFailure = createAction<{ error: string, listingResourceID: number }>('flowFest/buyFFCardFailure');

export const ffReservePackRequest = createAction('flowFest/ffReservePackRequest');
export const ffReservePackResponse = createAction('flowFest/ffReservePackResponse');
export const ffReservePackFailure = createAction<{ error: string }>('flowFest/ffReservePackFailure');

export const ffOpenPackRequest = createAction('flowFest/ffOpenPackRequest');
export const ffOpenPackResponse = createAction<FFOpenPackResponse>('flowFest/ffOpenPackResponse');
export const ffOpenPackFailure = createAction<{ error: string }>('flowFest/ffOpenPackFailure');

export const ffShowPackRequest = createAction('flowFest/ffShowPackRequest');
export const ffShowPackResponse = createAction<FFShowPackResponse>('flowFest/ffShowPackResponse');
export const ffShowPackFailure = createAction('flowFest/ffShowPackFailure');

export const getFlowFestPackStateRequest = createAction('flowFest/getFlowFestPackStateRequest');
export const getFlowFestPackStateResponse = createAction<GetFFPackStateResponse>('flowFest/getFlowFestPackStateResponse');
export const getFlowFestPackStateFailure = createAction<{ error: string }>('flowFest/getFlowFestPackStateFailure');

export const ffValidateCaptchaRequest = createAction<{ captcha_response: string }>('flowFest/ffValidateCaptchaRequest');
export const ffValidateCaptchaResponse = createAction<{ availability_time: string }>('flowFest/ffValidateCaptchaResponse');
export const ffValidateCaptchaFailure = createAction<{ error: string }>('flowFest/ffValidateCaptchaFailure');

export const redeemCodeRequest = createAction<{ code: string }>('flowFest/redeemCodeRequest');
export const redeemCodeResponse = createAction('flowFest/redeemCodeResponse');
export const redeemCodeFailure = createAction<{ error: string }>('flowFest/redeemCodeFailure');
export const redeemCodeReset = createAction('flowFest/redeemCodeReset');

export const ffViewCardRequest = createAction<FFViewCardRequestPayload>('flowFest/ffViewCardRequest');
export const ffViewPackRequest = createAction<FFViewPackRequestPayload>('flowFest/ffViewPackRequest');

export const ffClearReservePackRequest = createAction('flowFest/ffClearReservePackRequest');

export const flowFestCheckWalletRequest = createAction<{ wallet?: string }>('flowFest/checkWallet/request');
export const flowFestCheckWalletResponse = createAction<boolean>('flowFest/checkWallet/response');
export const flowFestCheckWalletFailure = createAction('flowFest/checkWallet/failure');

export const resetFlowFestState = createAction('flowFest/resetFlowFestState');
export const resetIsFFCardPurchased = createAction('flowFest/resetIsFFCardPurchased');
export const resetFFCards = createAction('flowFest/resetFFCards');

export const setIsFFCardsLoading = createAction<boolean>('flowFest/setIsFFCardsLoading');
export const setIsMoreFFCardsLoading = createAction<boolean>('flowFest/setIsMoreFFCardsLoading');

export const saleFFCardRequest = createAction<{
  projectId: FlowFestProjectId,
  itemId: number,
  price: number,
}>('ffCard/SALE_FFCARD_REQUEST');
export const saleFFCardResponse = createAction('ffCard/SALE_FFCARD_RESPONSE');
export const saleFFCardFailure = createAction('ffCard/SALE_FFCARD_FAILURE');

export const removeFromSaleFFCardRequest = createAction<RemoveFromSaleFFCardRequest>('ffCard/REMOVE_FROM_SALE_FF_CARD_REQUEST');
export const removeFromSaleFFCardResponse = createAction('ffCard/REMOVE_FROM_SALE_FF_CARD_RESPONSE');
export const removeFromSaleFFCardFailure = createAction('ffCard/REMOVE_FROM_SALE_FF_CARD_FAILURE');

export const loadMyFFCardsRequest = createAction<string>('ffCards/LOAD_MY_FF_CARDS_REQUEST');
export const loadMyFFCardsResponse = createAction<LoadMyFFCardsResponse>('ffCards/LOAD_MY_FF_CARDS_RESPONSE');
export const loadMyFFCardsFailure = createAction<any>('ffCards/LOAD_MY_FF_CARDS_FAILURE');
